import { findNode } from '../graph/graphModel.js';
import { loadAutomationModels } from '../automation/migration/LegacyEventsToAst.js';
import { SWITCH_ELSE_PORT, switchCasePort } from '../Hasp/properties/v2/hass/index.js';
import { applyTargetEntities } from './hassTargets.js';
import {
    getActionTargets,
    getBindingCase,
    getBindingEntityId,
    getBindingTargets,
    getConditionEntityId,
    getObjectPrimaryEntity,
    getRunWires,
    getSwitchEntityId,
    getTriggerEntityId,
    getTriggerSource,
    isStateTrigger,
    validateHassGraph,
} from './hassGraph.js';

// Triggers the validator flags with these codes are left out of the export.
const SKIPPED_TRIGGER_CODES = new Set(['trigger-no-source', 'trigger-no-event', 'trigger-duplicate']);
// Strategies whose binding reads an entity.
const ENTITY_STRATEGIES = new Set(['state', 'mapping']);

/**
 * Compile a page's Home Assistant flow graph into the HASSBuilder's
 * per-object format `{ primaryEntity, automation: AutomationModel-JSON[] }`
 * — the input of generateObjectYaml / HomeAssistantGenerator.
 *
 * Determinism: an object's binding models follow the order of binding nodes
 * in `graph.nodes`; its rule models follow its trigger nodes in node order,
 * and within a trigger the `order` of its run wires. That is the order the
 * HASSBuilder kept (bindings first, then rules). An action becomes one model
 * of its own; a condition becomes one model with its conditions and actions.
 * The bindings a switch gates become, per object and property, one 'switch'
 * binding at the place of the first of them, its cases in switch order; an
 * ungated binding of that property becomes its else case.
 * The generator groups models per event and puts conditioned models in one
 * `choose`, as before.
 *
 * A trigger on an entity's state belongs to no object: its models go to
 * `pageRules`, with a `source: 'state'` trigger.
 *
 * @param {object} graph
 * @param {object} context  { getObject(uuid), pageId } — pageId is the page's OpenHASP id
 * @returns {{ objects: Map<string, { primaryEntity: string, automation: object[] }>, pageRules: object[], diagnostics: object[] }}
 */
export function compileHassGraph(graph, context = {}) {
    const diagnostics = validateHassGraph(graph, context);
    const skipped = new Set(diagnostics.filter(item => SKIPPED_TRIGGER_CODES.has(item.code)).map(item => item.nodeId));
    const objects = new Map();
    const bindingModels = new Map();
    const ruleModels = new Map();
    const pageRules = [];
    const push = (map, uuid, model) => {
        if (!map.has(uuid)) map.set(uuid, []);
        map.get(uuid).push(model);
    };
    const widget = uuid => {
        const object = context.getObject?.(uuid);
        const pageId = context.pageId ?? null;
        return {
            widgetPageId: pageId,
            widgetHaspId: object?.haspid ?? null,
            widgetType: object?.obj ?? null,
            widgetId: object && pageId !== null ? `p${pageId}b${object.haspid}` : null,
        };
    };
    const actionJson = node => applyTargetEntities({ ...structuredClone(node.data), id: node.id }, getActionTargets(graph, node.id));
    // A condition's entity operands compare the entity resolved from the graph.
    const conditionJson = node => {
        const entityId = getConditionEntityId(graph, node.id);
        const operand = value => (value?.kind === 'entity' ? { ...value, entityId } : value);
        return (node.data?.conditions ?? []).map(condition => ({ ...condition, left: operand(condition.left), right: operand(condition.right) }));
    };
    const ruleModel = (uuid, trigger, { id, actions, conditions = [], filter = null }) => {
        if (uuid === null) {
            const stateTrigger = { id: trigger.id, source: 'state', event: '', entityId: getTriggerEntityId(graph, trigger.id) };
            for (const key of ['to', 'from', 'attribute']) if (trigger.data?.[key]) stateTrigger[key] = trigger.data[key];
            return { id, triggers: [stateTrigger], actions, conditions: structuredClone(conditions) };
        }
        const { widgetId, ...meta } = widget(uuid);
        return {
            id,
            ...meta,
            triggers: [{
                id: trigger.id,
                source: 'widget',
                event: trigger.data.event,
                ...(filter !== null && filter !== undefined ? { filter: structuredClone(filter) } : {}),
                ...(widgetId !== null ? { widgetId } : {}),
            }],
            actions,
            conditions: structuredClone(conditions),
        };
    };

    // One 'switch' binding per object, property and switch; cases collect per output port.
    const switchBindings = new Map();
    const gatedBinding = (uuid, switchNode, property) => {
        const key = `${uuid}\u0000${property}\u0000${switchNode.id}`;
        if (!switchBindings.has(key)) {
            const { widgetId, ...meta } = widget(uuid);
            const binding = {
                id: `${switchNode.id}:${property}`, property, strategy: 'switch',
                entityId: getSwitchEntityId(graph, switchNode.id), attribute: switchNode.data?.attribute ?? '', cases: new Map(),
            };
            const model = {
                id: `${switchNode.id}:${property}@${uuid}`, ...meta, triggers: [], actions: [], conditions: [], bindings: [binding],
            };
            switchBindings.set(key, { uuid, switchNode, binding, model });
            push(bindingModels, uuid, model);
        }
        return switchBindings.get(key).binding;
    };

    for (const node of graph.nodes) {
        if (node.kind === 'binding' && node.data?.property) {
            const gate = getBindingCase(graph, node.id);
            const switchNode = gate ? findNode(graph, gate.switchId) : null;
            for (const uuid of getBindingTargets(graph, node.id)) {
                const binding = { ...structuredClone(node.data), id: node.id };
                if (ENTITY_STRATEGIES.has(node.data.strategy)) {
                    binding.entityId = getBindingEntityId(graph, node) || (switchNode ? getSwitchEntityId(graph, switchNode.id) : '');
                }
                if (switchNode) {
                    const cases = gatedBinding(uuid, switchNode, node.data.property).cases;
                    if (!cases.has(gate.port)) cases.set(gate.port, binding);
                    continue;
                }
                const { widgetId, ...meta } = widget(uuid);
                push(bindingModels, uuid, {
                    id: `${node.id}@${uuid}`, ...meta, triggers: [], actions: [], conditions: [], bindings: [binding],
                });
            }
        } else if (node.kind === 'trigger' && !skipped.has(node.id)) {
            // A state trigger's models are page rules (uuid null).
            const uuid = isStateTrigger(graph, node.id) ? null : getTriggerSource(graph, node.id);
            const add = model => (uuid === null ? pageRules.push(model) : push(ruleModels, uuid, model));
            for (const wire of getRunWires(graph, node.id)) {
                const target = findNode(graph, wire.to.node);
                if (target?.kind === 'action') {
                    add(ruleModel(uuid, node, { id: `${node.id}:${target.id}`, actions: [actionJson(target)] }));
                } else if (target?.kind === 'condition') {
                    const actions = getRunWires(graph, target.id)
                        .map(item => findNode(graph, item.to.node))
                        .filter(action => action?.kind === 'action')
                        .map(actionJson);
                    add(ruleModel(uuid, node, {
                        id: `${node.id}:${target.id}`, actions, conditions: conditionJson(target), filter: target.data?.filter,
                    }));
                }
            }
        }
    }

    // Cases in switch order, else last; a wire from a removed case is left out.
    for (const { uuid, switchNode, binding, model } of switchBindings.values()) {
        const order = [
            ...(switchNode.data?.cases ?? []).map(item => ({ port: switchCasePort(item.id), when: { operator: item.operator, value: item.value } })),
            { port: SWITCH_ELSE_PORT, when: null },
        ];
        const fallback = binding.cases.has(SWITCH_ELSE_PORT) ? null
            : bindingModels.get(uuid).find(item => item.bindings[0].strategy !== 'switch' && item.bindings[0].property === binding.property);
        binding.cases = order.filter(item => binding.cases.has(item.port))
            .map(item => ({ when: item.when, binding: binding.cases.get(item.port) }));
        if (!fallback) continue;
        // The ungated binding is the else; the merged binding keeps the earlier place.
        binding.cases.push({ when: null, binding: fallback.bindings[0] });
        const list = bindingModels.get(uuid);
        const [first, second] = [list.indexOf(fallback), list.indexOf(model)].sort((a, b) => a - b);
        list.splice(second, 1);
        list[first] = model;
    }

    const uuids = new Set([...bindingModels.keys(), ...ruleModels.keys()]);
    for (const wire of graph.wires) if (wire.to.object !== undefined && wire.to.port === 'in') uuids.add(wire.to.object);
    for (const uuid of uuids) {
        if (!context.getObject?.(uuid)) continue;
        objects.set(uuid, {
            primaryEntity: getObjectPrimaryEntity(graph, uuid),
            automation: [...(bindingModels.get(uuid) ?? []), ...(ruleModels.get(uuid) ?? [])],
        });
    }
    return { objects, pageRules, diagnostics };
}

/**
 * The configuration an object exports: what stays in its `hass_config`
 * (legacy popup rules, settings such as chart_series) plus what its page's
 * flow graph compiles to for it. Stored rules come first. Legacy rows
 * (`events[]`/`properties[]`) next to compiled rules are converted first, so
 * the compiled rules never hide them. `source` ({ page_id, hasp_id, object })
 * is only needed for that conversion.
 */
export function mergeHassConfig(stored, compiled, source = {}) {
    if (!compiled || (compiled.automation.length === 0 && !compiled.primaryEntity)) return stored;
    const base = stored ?? {};
    const storedAutomation = (base.automation ?? []).length > 0
        ? base.automation
        : loadAutomationModels({ ...source, hass_config: base }).map(model => model.toJSON());
    const merged = { ...base, automation: [...storedAutomation, ...compiled.automation] };
    if (compiled.primaryEntity) merged.primaryEntity = compiled.primaryEntity;
    return merged;
}
